// src/pages/DomainsPage.jsx
import React from "react";
import {
    Paper,
    Menu,
    MenuItem,
    ListItemIcon,
    ListItemText,
    TextField,
    TableContainer,
    Table,
    TableHead,
    TableRow,
    TableCell,
    TableBody,
    Button,
    Box,
    CircularProgress,
    Alert,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TableSortLabel,
    IconButton,
    Typography,
    Divider,
    Tab,
    Tabs,
    Checkbox,
    FormControlLabel,
} from "@mui/material";
import {
    Add as AddIcon,
    Edit as EditIcon,
    ContentCopy as ContentCopyIcon,
    MoreVert as MoreVertIcon,
    Delete as DeleteIcon,
} from "@mui/icons-material";
import { Editor } from "@tinymce/tinymce-react";
import DomainBrandingUpload from "./DomainBrandingUpload";
import PageContainer from "../layout/PageContainer";
import axios from "axios";
import { DOMAIN_COLORS, getDomainColors } from "../../../theme";

const BRANDING = [{ key: "header_logo", label: "Header-Logo" }, { key: "footer_logo", label: "Footer-Logo" }, { key: "favicon", label: "Favicon" }];
const API_BASE = process.env.REACT_APP_BACKEND_URL || "";
const FOOTER_LANGS = [
    { key: "de", label: "Deutsch" },
    { key: "en", label: "Englisch" },
    { key: "fr", label: "Französisch" },
    { key: "it", label: "Italienisch" },
];

// ---- Sort utils ----
function getCellValue(row, key) {
    switch (key) {
        case "id":
            return row.id ?? 0;
        case "url":
            return row.url ?? "";
        case "page_title":
            return row.page_title ?? "";
        case "meta_keywords":
            return row.meta_keywords ?? "";
        case "meta_description":
            return row.meta_description ?? "";
        case "page_header_image":
            return row.page_header_image ?? "";
        case "header_title":
            return row.header_title ?? "";
        case "header_subline":
            return row.header_subline ?? "";
        case "popular_searches":
            return row.popular_searches ?? "";
        case "footer_intro_de":
            return row.footer_intro_de ?? "";
        case "footer_contact_de":
            return row.footer_contact_de ?? "";
        case "footer_info_links_de":
            return row.footer_info_links_de ?? "";
        case "footer_tourism_links_de":
            return row.footer_tourism_links_de ?? "";
        default:
            return "";
    }
}

function descendingComparator(a, b, orderBy) {
    const va = getCellValue(a, orderBy);
    const vb = getCellValue(b, orderBy);

    const na = typeof va === "number" ? va : Number(va);
    const nb = typeof vb === "number" ? vb : Number(vb);

    if (Number.isFinite(na) && Number.isFinite(nb)) {
        return nb - na;
    }

    return (vb ?? "").toString().localeCompare((va ?? "").toString(), "de", {
        sensitivity: "base",
    });
}

function getComparator(order, orderBy) {
    return order === "desc"
        ? (a, b) => descendingComparator(a, b, orderBy)
        : (a, b) => -descendingComparator(a, b, orderBy);
}

function stableSort(array, comparator) {
    return [...array]
        .map((el, idx) => [el, idx])
        .sort((a, b) => comparator(a[0], b[0]) || a[1] - b[1])
        .map((el) => el[0]);
}

function cleanOpt(v) {
    const s = (v || "").trim();
    return s ? s : null;
}

function buildApiUrl(path) {
    return `${API_BASE}${path}`;
}

function parseFooterLinkConfig(value) {
    const raw = String(value || "").trim();
    if (!raw) return [];

    try {
        const parsed = JSON.parse(raw);
        if (Array.isArray(parsed)) {
            return parsed.map((item) => ({
                label: String(item?.label || "").trim(),
                url: String(item?.url || item?.href || "").trim(),
                new_window: !!item?.new_window,
            })).filter((item) => item.label || item.url);
        }
    } catch {
        // fall back to legacy line format below
    }

    return raw
        .split(/\r?\n/)
        .map((line) => line.trim())
        .filter(Boolean)
        .map((line) => {
            const [label, ...rest] = line.split("|");
            return {
                label: (label || "").trim(),
                url: (rest.join("|") || "#").trim() || "#",
                new_window: false,
            };
        })
        .filter((item) => item.label || item.url);
}

function serializeFooterLinkConfig(rows) {
    const clean = (rows || [])
        .map((row) => ({
            label: String(row.label || "").trim(),
            url: String(row.url || "").trim(),
            new_window: !!row.new_window,
        }))
        .filter((row) => row.label || row.url);
    return clean.length ? JSON.stringify(clean) : "";
}

function FooterLinksEditor({ title, value, onChange }) {
    const rows = React.useMemo(() => parseFooterLinkConfig(value), [value]);
    const normalizedRows = rows.length ? rows : [{ label: "", url: "", new_window: false }];

    const updateRows = (nextRows) => onChange(serializeFooterLinkConfig(nextRows));
    const updateRow = (index, patch) => {
        updateRows(normalizedRows.map((row, i) => (i === index ? { ...row, ...patch } : row)));
    };

    return (
        <Box sx={{ border: "1px solid", borderColor: "divider", borderRadius: 1.5, p: 1.5 }}>
            <Typography variant="body2" sx={{ fontWeight: 700, mb: 1.2 }}>
                {title}
            </Typography>
            <Box sx={{ display: "grid", gap: 1.2 }}>
                {normalizedRows.map((row, index) => (
                    <Box
                        key={index}
                        sx={{
                            display: "grid",
                            gridTemplateColumns: { xs: "1fr", md: "1fr 1fr auto auto" },
                            gap: 1,
                            alignItems: "center",
                        }}
                    >
                        <TextField
                            label="Label"
                            size="small"
                            value={row.label}
                            onChange={(e) => updateRow(index, { label: e.target.value })}
                        />
                        <TextField
                            label="Link"
                            size="small"
                            value={row.url}
                            onChange={(e) => updateRow(index, { url: e.target.value })}
                        />
                        <FormControlLabel
                            sx={{ m: 0, whiteSpace: "nowrap" }}
                            control={
                                <Checkbox
                                    checked={!!row.new_window}
                                    onChange={(e) => updateRow(index, { new_window: e.target.checked })}
                                />
                            }
                            label="Neues Fenster"
                        />
                        <IconButton
                            color="error"
                            disabled={normalizedRows.length <= 1}
                            onClick={() => updateRows(normalizedRows.filter((_, i) => i !== index))}
                        >
                            <DeleteIcon fontSize="small" />
                        </IconButton>
                    </Box>
                ))}
            </Box>
            <Button
                startIcon={<AddIcon />}
                sx={{ mt: 1.2 }}
                onClick={() => updateRows([...normalizedRows, { label: "", url: "", new_window: false }])}
            >
                Link hinzufügen
            </Button>
        </Box>
    );
}

function WysiwygField({ label, value, onChange, height = 180 }) {
    const init = React.useMemo(
        () => ({
            license_key: "gpl",
            height,
            menubar: false,
            branding: false,
            statusbar: false,
            plugins: "lists link autolink",
            toolbar: "undo redo | bold italic underline | bullist numlist | link | removeformat",
            content_style:
                "body { font-family: Inter, Arial, sans-serif; font-size: 14px; padding: 8px; } p { margin: 0 0 8px; }",
        }),
        [height]
    );

    return (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75 }}>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                {label}
            </Typography>
            <Box
                sx={{
                    border: "1px solid",
                    borderColor: "divider",
                    borderRadius: 1.5,
                    overflow: "hidden",
                    backgroundColor: "background.paper",
                }}
            >
                <Editor
                    tinymceScriptSrc={`${process.env.PUBLIC_URL}/tinymce/tinymce.min.js`}
                    value={value ?? ""}
                    init={init}
                    onEditorChange={(html) => onChange(html)}
                />
            </Box>
        </Box>
    );
}

export default function DomainsPage() {
    const [brandingFiles, setBrandingFiles] = React.useState({});
    const [domains, setDomains] = React.useState([]);
    const [actionMenu, setActionMenu] = React.useState(null);
    const closeActionMenu = () => setActionMenu(null);
    const [loading, setLoading] = React.useState(false);
    const [error, setError] = React.useState("");

    const [order, setOrder] = React.useState("asc");
    const [orderBy, setOrderBy] = React.useState("url");

    const [open, setOpen] = React.useState(false);
    const [form, setForm] = React.useState({
        ...getDomainColors(),
        url: "",
        meta_keywords: "",
        page_title: "",
        header_title: "",
        header_subline: "",
        popular_searches: "",
        footer_intro_de: "",
        footer_intro_en: "",
        footer_intro_fr: "",
        footer_intro_it: "",
        footer_contact_de: "",
        footer_contact_en: "",
        footer_contact_fr: "",
        footer_contact_it: "",
        footer_info_links_de: "",
        footer_info_links_en: "",
        footer_info_links_fr: "",
        footer_info_links_it: "",
        footer_tourism_links_de: "",
        footer_tourism_links_en: "",
        footer_tourism_links_fr: "",
        footer_tourism_links_it: "",
        meta_description: "",
    });
    const [footerLangTab, setFooterLangTab] = React.useState("de");
    const [editingDomain, setEditingDomain] = React.useState(null);
    const [copiedDomain, setCopiedDomain] = React.useState(null);
    const existingHeaderImage = (editingDomain || copiedDomain)?.page_header_image;
    const [submitting, setSubmitting] = React.useState(false);
    const [submitError, setSubmitError] = React.useState("");

    const [deleteDialogOpen, setDeleteDialogOpen] = React.useState(false);
    const [deleteTarget, setDeleteTarget] = React.useState(null);
    const [deleteError, setDeleteError] = React.useState("");
    const [deleting, setDeleting] = React.useState(false);

    // Upload state
    const [imageFile, setImageFile] = React.useState(null);
    const [imagePreview, setImagePreview] = React.useState("");
    const [removeImage, setRemoveImage] = React.useState(false);

    const fetchDomains = React.useCallback(async () => {
        setError("");
        setLoading(true);
        try {
            const res = await axios.get(buildApiUrl("/api/domains"));
            setDomains(Array.isArray(res.data) ? res.data : []);
        } catch (e) {
            setError(e?.response?.data?.error || e.message || "Laden fehlgeschlagen.");
        } finally {
            setLoading(false);
        }
    }, []);

    React.useEffect(() => {
        fetchDomains();
    }, [fetchDomains]);

    React.useEffect(() => {
        // English comment: Clean up blob preview URL
        return () => {
            if (imagePreview) URL.revokeObjectURL(imagePreview);
        };
    }, [imagePreview]);

    const handleOpenCreate = () => {
        setBrandingFiles({});
        setEditingDomain(null);
        setCopiedDomain(null);
        setForm({
            ...getDomainColors(),
            url: "",
            meta_keywords: "",
            page_title: "",
            header_title: "",
            header_subline: "",
            popular_searches: "",
            footer_intro_de: "",
            footer_intro_en: "",
            footer_intro_fr: "",
            footer_intro_it: "",
            footer_contact_de: "",
            footer_contact_en: "",
            footer_contact_fr: "",
            footer_contact_it: "",
            footer_info_links_de: "",
            footer_info_links_en: "",
            footer_info_links_fr: "",
            footer_info_links_it: "",
            footer_tourism_links_de: "",
            footer_tourism_links_en: "",
            footer_tourism_links_fr: "",
            footer_tourism_links_it: "",
            meta_description: "",
        });
        setFooterLangTab("de");
        setSubmitError("");
        setImageFile(null);
        if (imagePreview) URL.revokeObjectURL(imagePreview);
        setImagePreview("");
        setRemoveImage(false);
        setOpen(true);
    };

    const handleOpenEdit = (d, copy = false) => {
        setBrandingFiles({});
        setEditingDomain(copy ? null : d);
        setCopiedDomain(copy ? d : null);
        setForm({
            ...getDomainColors(d),
            ...Object.fromEntries(BRANDING.map(({ key }) => [key, d[key] || null])),
            url: copy ? "" : d.url || "",
            meta_keywords: d.meta_keywords || "",
            page_title: d.page_title || "",
            header_title: d.header_title || "",
            header_subline: d.header_subline || "",
            popular_searches: d.popular_searches || "",
            footer_intro_de: d.footer_intro_de || "",
            footer_intro_en: d.footer_intro_en || "",
            footer_intro_fr: d.footer_intro_fr || "",
            footer_intro_it: d.footer_intro_it || "",
            footer_contact_de: d.footer_contact_de || "",
            footer_contact_en: d.footer_contact_en || "",
            footer_contact_fr: d.footer_contact_fr || "",
            footer_contact_it: d.footer_contact_it || "",
            footer_info_links_de: d.footer_info_links_de || "",
            footer_info_links_en: d.footer_info_links_en || "",
            footer_info_links_fr: d.footer_info_links_fr || "",
            footer_info_links_it: d.footer_info_links_it || "",
            footer_tourism_links_de: d.footer_tourism_links_de || "",
            footer_tourism_links_en: d.footer_tourism_links_en || "",
            footer_tourism_links_fr: d.footer_tourism_links_fr || "",
            footer_tourism_links_it: d.footer_tourism_links_it || "",
            meta_description: d.meta_description || "",
        });
        setFooterLangTab("de");
        setSubmitError("");
        setImageFile(null);
        if (imagePreview) URL.revokeObjectURL(imagePreview);
        setImagePreview("");
        setRemoveImage(false);
        setOpen(true);
    };

    const handlePickFile = (ev) => {
        const f = ev.target.files?.[0];
        if (!f) return;

        const ok = ["image/png", "image/jpeg", "image/webp"].includes(f.type);
        if (!ok) {
            setSubmitError("Bitte PNG, JPG/JPEG oder WEBP auswählen.");
            return;
        }

        if (imagePreview) URL.revokeObjectURL(imagePreview);
        setImageFile(f);
        setImagePreview(URL.createObjectURL(f));
        setRemoveImage(false);
    };

    const handleClearFile = () => {
        if (imagePreview) URL.revokeObjectURL(imagePreview);
        setImageFile(null);
        setImagePreview("");
    };

    const handleSave = async () => {
        setSubmitError("");

        const url = (form.url || "").trim();
        if (!url) {
            setSubmitError("URL ist Pflichtfeld.");
            return;
        }

        if (DOMAIN_COLORS.some(({ key }) => !/^#[0-9a-f]{6}$/i.test(form[key] || ""))) {
            setSubmitError("Bitte alle Farben im Format #RRGGBB angeben.");
            return;
        }
        const payload = {
            ...getDomainColors(form),
            ...Object.fromEntries(BRANDING.map(({ key }) => [key, form[key] || null])),
            ...(copiedDomain ? { page_header_image: removeImage ? null : existingHeaderImage || null } : {}),
            url,
            meta_keywords: cleanOpt(form.meta_keywords),
            page_title: cleanOpt(form.page_title),
            header_title: cleanOpt(form.header_title),
            header_subline: cleanOpt(form.header_subline),
            popular_searches: cleanOpt(form.popular_searches),
            footer_intro_de: cleanOpt(form.footer_intro_de),
            footer_intro_en: cleanOpt(form.footer_intro_en),
            footer_intro_fr: cleanOpt(form.footer_intro_fr),
            footer_intro_it: cleanOpt(form.footer_intro_it),
            footer_contact_de: cleanOpt(form.footer_contact_de),
            footer_contact_en: cleanOpt(form.footer_contact_en),
            footer_contact_fr: cleanOpt(form.footer_contact_fr),
            footer_contact_it: cleanOpt(form.footer_contact_it),
            footer_info_links_de: cleanOpt(form.footer_info_links_de),
            footer_info_links_en: cleanOpt(form.footer_info_links_en),
            footer_info_links_fr: cleanOpt(form.footer_info_links_fr),
            footer_info_links_it: cleanOpt(form.footer_info_links_it),
            footer_tourism_links_de: cleanOpt(form.footer_tourism_links_de),
            footer_tourism_links_en: cleanOpt(form.footer_tourism_links_en),
            footer_tourism_links_fr: cleanOpt(form.footer_tourism_links_fr),
            footer_tourism_links_it: cleanOpt(form.footer_tourism_links_it),
            meta_description: cleanOpt(form.meta_description),
        };

        setSubmitting(true);
        try {
            let domainRow = null;

            if (editingDomain) {
                const res = await axios.patch(buildApiUrl(`/api/domains/${editingDomain.id}`), payload);
                domainRow = res.data;
            } else {
                const res = await axios.post(buildApiUrl("/api/domains"), payload);
                domainRow = res.data;
            }

            // Keep the created ID if a subsequent upload fails, so retry updates it.
            setEditingDomain(domainRow);
            setCopiedDomain(null);
            for (const { key } of BRANDING) {
                if (!brandingFiles[key]) continue;
                const data = new FormData();
                data.append("file", brandingFiles[key]);
                const result = await axios.post(buildApiUrl(`/api/domains/${domainRow.id}/branding/${key}`), data);
                domainRow = result.data;
                const uploadedPath = domainRow[key];
                setForm((current) => ({ ...current, [key]: uploadedPath }));
                setBrandingFiles((current) => { const next = { ...current }; delete next[key]; return next; });
            }

            // Remove existing image if requested (and no new upload)
            if (editingDomain && removeImage && !imageFile) {
                const res = await axios.patch(buildApiUrl(`/api/domains/${editingDomain.id}`), {
                    page_header_image: null,
                });
                domainRow = res.data;
            }

            // Upload new image if selected
            if (imageFile && domainRow?.id) {
                const formData = new FormData();
                formData.append("file", imageFile);

                const up = await axios.post(
                    buildApiUrl(`/api/domains/${domainRow.id}/page-header-image`),
                    formData,
                    { headers: { "Content-Type": "multipart/form-data" } }
                );
                domainRow = up.data;
            }

            setOpen(false);
            await fetchDomains();
        } catch (e) {
            setSubmitError(e?.response?.data?.error || e.message || "Speichern fehlgeschlagen.");
        } finally {
            setSubmitting(false);
        }
    };

    const rows = React.useMemo(
        () => stableSort(domains || [], getComparator(order, orderBy)),
        [domains, order, orderBy]
    );

    const columns = [
        { id: "id", label: "ID" },
        { id: "url", label: "URL" },
        { id: "page_title", label: "Page-Title" },
        { id: "header_title", label: "Header Titel" },
        { id: "page_header_image", label: "Header Image" },
        { id: "meta_description", label: "Meta-Description" },
    ];

    return (
        <PageContainer
            title="Domains"
            actions={
                <Box sx={{ display: "flex", gap: 1 }}>
                    <Button variant="outlined" onClick={fetchDomains} disabled={loading}>
                        Aktualisieren
                    </Button>
                    <Button variant="contained" onClick={handleOpenCreate}>
                        Neue Domain
                    </Button>
                </Box>
            }
        >
            {error && <Alert severity="error">{error}</Alert>}

            <TableContainer component={Paper}>
                {loading ? (
                    <Box sx={{ py: 6, textAlign: "center" }}>
                        <CircularProgress />
                    </Box>
                ) : (
                    <Table>
                        <TableHead>
                            <TableRow>
                                {columns.map((c) => (
                                    <TableCell key={c.id}>
                                        <TableSortLabel
                                            active={orderBy === c.id}
                                            direction={order}
                                            onClick={() => {
                                                const isAsc = orderBy === c.id && order === "asc";
                                                setOrder(isAsc ? "desc" : "asc");
                                                setOrderBy(c.id);
                                            }}
                                        >
                                            {c.label}
                                        </TableSortLabel>
                                    </TableCell>
                                ))}
                                <TableCell align="right">Aktionen</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {rows.map((d) => (
                                <TableRow key={d.id} hover>
                                    <TableCell>{d.id}</TableCell>
                                    <TableCell>
                                        <Typography variant="body2" sx={{ fontFamily: "monospace" }}>
                                            {d.url}
                                        </Typography>
                                    </TableCell>
                                    <TableCell>{d.page_title || "-"}</TableCell>
                                    <TableCell>{d.header_title || "-"}</TableCell>
                                    <TableCell>
                                        {d.page_header_image ? (
                                            <a href={buildApiUrl(d.page_header_image)} target="_blank" rel="noreferrer">
                                                anzeigen
                                            </a>
                                        ) : (
                                            "-"
                                        )}
                                    </TableCell>
                                    <TableCell>
                                        {d.meta_description ? (
                                            <Typography variant="body2" sx={{ maxWidth: 520 }}>
                                                {d.meta_description}
                                            </Typography>
                                        ) : (
                                            "-"
                                        )}
                                    </TableCell>

                                    <TableCell align="right">
                                        <IconButton
                                            aria-label={`Aktionen für ${d.url}`}
                                            aria-controls={actionMenu?.domain.id === d.id ? "domain-actions-menu" : undefined}
                                            aria-haspopup="menu"
                                            aria-expanded={actionMenu?.domain.id === d.id ? "true" : undefined}
                                            onClick={(event) => setActionMenu({ anchorEl: event.currentTarget, domain: d })}
                                        >
                                            <MoreVertIcon />
                                        </IconButton>
                                    </TableCell>
                                </TableRow>
                            ))}
                            {!rows.length && (
                                <TableRow>
                                    <TableCell colSpan={columns.length + 1}>
                                        <Typography variant="body2" color="text.secondary">
                                            Keine Domains vorhanden.
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                )}
            </TableContainer>

            <Menu
                id="domain-actions-menu"
                anchorEl={actionMenu?.anchorEl || null}
                open={Boolean(actionMenu)}
                onClose={closeActionMenu}
            >
                <MenuItem onClick={() => { handleOpenEdit(actionMenu.domain); closeActionMenu(); }}>
                    <ListItemIcon><EditIcon fontSize="small" /></ListItemIcon>
                    <ListItemText>Bearbeiten</ListItemText>
                </MenuItem>
                <MenuItem onClick={() => { handleOpenEdit(actionMenu.domain, true); closeActionMenu(); }}>
                    <ListItemIcon><ContentCopyIcon fontSize="small" /></ListItemIcon>
                    <ListItemText>Kopieren</ListItemText>
                </MenuItem>
                <Divider />
                <MenuItem sx={{ color: "error.main" }} onClick={() => {
                    setDeleteTarget(actionMenu.domain);
                    setDeleteDialogOpen(true);
                    setDeleteError("");
                    closeActionMenu();
                }}>
                    <ListItemIcon sx={{ color: "inherit" }}><DeleteIcon fontSize="small" /></ListItemIcon>
                    <ListItemText>Löschen</ListItemText>
                </MenuItem>
            </Menu>

            <Dialog open={open} onClose={() => !submitting && setOpen(false)} fullWidth maxWidth="xl">
                <DialogTitle>
                    {editingDomain ? "Domain bearbeiten" : copiedDomain ? "Domain kopieren" : "Neue Domain anlegen"}
                </DialogTitle>

                <DialogContent dividers>
                    {copiedDomain && (
                        <Alert severity="info" sx={{ mb: 2 }}>
                            Einstellungen von {copiedDomain.url} übernommen. Bitte die URL der neuen Domain eingeben.
                        </Alert>
                    )}
                    <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2 }}>
                        <TextField
                            fullWidth
                            label="URL*"
                            autoFocus={!!copiedDomain}
                            helperText="z.B. https://example.de"
                            value={form.url}
                            onChange={(e) => setForm((f) => ({ ...f, url: e.target.value }))}
                        />
                        <TextField
                            fullWidth
                            label="Page-Title"
                            value={form.page_title}
                            onChange={(e) => setForm((f) => ({ ...f, page_title: e.target.value }))}
                        />
                        <TextField
                            fullWidth
                            label="Meta-Keywords"
                            helperText="Kommagetrennt"
                            value={form.meta_keywords}
                            onChange={(e) => setForm((f) => ({ ...f, meta_keywords: e.target.value }))}
                        />
                        <TextField
                            fullWidth
                            label="Meta-Description"
                            value={form.meta_description}
                            onChange={(e) => setForm((f) => ({ ...f, meta_description: e.target.value }))}
                        />
                    </Box>

                    <Divider sx={{ my: 2 }} />

                    <Typography variant="subtitle2" sx={{ mb: 2 }}>Logos und Favicon</Typography>
                    <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "repeat(3, 1fr)" }, gap: 2, mb: 2 }}>
                        {BRANDING.map(({ key, label }) => (
                            <DomainBrandingUpload key={key} label={label} value={form[key]} file={brandingFiles[key]}
                                favicon={key === "favicon"} disabled={submitting}
                                onFile={(file) => {
                                    const allowed = key === "favicon" ? /\.(png|jpe?g|webp|ico)$/i : /\.(png|jpe?g|webp)$/i;
                                    if (!allowed.test(file.name) || file.size > 5 * 1024 * 1024) {
                                        setSubmitError("Bitte ein unterstütztes Bildformat mit maximal 5 MB auswählen.");
                                        return;
                                    }
                                    setSubmitError("");
                                    setBrandingFiles((current) => ({ ...current, [key]: file }));
                                }}
                                onRemove={() => {
                                    setBrandingFiles((current) => { const next = { ...current }; delete next[key]; return next; });
                                    setForm((current) => ({ ...current, [key]: null }));
                                }} />
                        ))}
                    </Box>
                    <Divider sx={{ my: 2 }} />

                    <Typography variant="subtitle2" sx={{ mb: 2 }}>Farbschema</Typography>
                    <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr 1fr" }, gap: 2 }}>
                        {DOMAIN_COLORS.map(({ key, label, defaultValue }) => (
                            <Box key={key} sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                <input type="color" aria-label={`${label} auswählen`}
                                    value={/^#[0-9a-f]{6}$/i.test(form[key] || "") ? form[key] : defaultValue}
                                    onChange={(e) => setForm((f) => ({ ...f, [key]: e.target.value }))} />
                                <TextField fullWidth label={label} value={form[key] || ""}
                                    error={!/^#[0-9a-f]{6}$/i.test(form[key] || "")}
                                    helperText="#RRGGBB"
                                    onChange={(e) => setForm((f) => ({ ...f, [key]: e.target.value }))} />
                            </Box>
                        ))}
                    </Box>
                    <Button onClick={() => setForm((f) => ({ ...f, ...getDomainColors() }))}>Standardfarben wiederherstellen</Button>
                    <Divider sx={{ my: 2 }} />

                    <Typography variant="subtitle2" sx={{ mb: 1 }}>
                        Header Inhalt
                    </Typography>

                    <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2 }}>
                        <WysiwygField
                            label="Header Titel"
                            value={form.header_title}
                            onChange={(html) => setForm((f) => ({ ...f, header_title: html }))}
                            height={170}
                        />
                        <WysiwygField
                            label="Header Subline"
                            value={form.header_subline}
                            onChange={(html) => setForm((f) => ({ ...f, header_subline: html }))}
                            height={170}
                        />
                        <TextField
                            fullWidth
                            multiline
                            minRows={3}
                            label="Beliebte Suchen"
                            helperText="Eine Suche pro Zeile oder kommagetrennt"
                            value={form.popular_searches}
                            onChange={(e) => setForm((f) => ({ ...f, popular_searches: e.target.value }))}
                            sx={{ gridColumn: "1 / -1" }}
                        />
                    </Box>

                    <Divider sx={{ my: 2 }} />

                    <Typography variant="subtitle2" sx={{ mb: 1 }}>
                        Footer Inhalt
                    </Typography>

                    <Tabs value={footerLangTab} onChange={(_, value) => setFooterLangTab(value)} sx={{ mb: 2 }}>
                        {FOOTER_LANGS.map((lang) => (
                            <Tab key={lang.key} value={lang.key} label={lang.label} />
                        ))}
                    </Tabs>

                    {FOOTER_LANGS.map((lang) => (
                        <Box
                            key={lang.key}
                            sx={{
                                display: footerLangTab === lang.key ? "grid" : "none",
                                gridTemplateColumns: "1fr 1fr",
                                gap: 2,
                            }}
                        >
                            <WysiwygField
                                label={`Text erste Spalte (${lang.key.toUpperCase()})`}
                                value={form[`footer_intro_${lang.key}`]}
                                onChange={(html) => setForm((f) => ({ ...f, [`footer_intro_${lang.key}`]: html }))}
                                height={210}
                            />
                            <WysiwygField
                                label={`Kontaktinformationen (${lang.key.toUpperCase()})`}
                                value={form[`footer_contact_${lang.key}`]}
                                onChange={(html) => setForm((f) => ({ ...f, [`footer_contact_${lang.key}`]: html }))}
                                height={210}
                            />
                            <FooterLinksEditor
                                title={`Menüpunkte Schweizer Info & News (${lang.key.toUpperCase()})`}
                                value={form[`footer_info_links_${lang.key}`]}
                                onChange={(next) => setForm((f) => ({ ...f, [`footer_info_links_${lang.key}`]: next }))}
                            />
                            <FooterLinksEditor
                                title={`Menüpunkte Tourismus Angebote (${lang.key.toUpperCase()})`}
                                value={form[`footer_tourism_links_${lang.key}`]}
                                onChange={(next) => setForm((f) => ({ ...f, [`footer_tourism_links_${lang.key}`]: next }))}
                            />
                        </Box>
                    ))}

                    <Divider sx={{ my: 2 }} />

                    <Typography variant="subtitle2" sx={{ mb: 1 }}>
                        Page-Header Image
                    </Typography>

                    <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2 }}>
                        <Box>
                            <Button variant="outlined" component="label" disabled={submitting}>
                                Bild auswählen…
                                <input
                                    hidden
                                    type="file"
                                    accept="image/png,image/jpeg,image/webp"
                                    onChange={handlePickFile}
                                />
                            </Button>

                            <Box sx={{ mt: 1, display: "flex", gap: 1, flexWrap: "wrap", alignItems: "center" }}>
                                {imageFile ? (
                                    <Typography variant="body2" color="text.secondary">
                                        Ausgewählt: {imageFile.name}
                                    </Typography>
                                ) : null}

                                {imageFile ? (
                                    <Button size="small" onClick={handleClearFile} disabled={submitting}>
                                        Auswahl verwerfen
                                    </Button>
                                ) : null}

                                {existingHeaderImage ? (
                                    <Button
                                        size="small"
                                        color="error"
                                        disabled={submitting}
                                        onClick={() => {
                                            setRemoveImage(true);
                                            handleClearFile();
                                        }}
                                    >
                                        Bild entfernen
                                    </Button>
                                ) : null}
                            </Box>

                            {existingHeaderImage && !imageFile && !removeImage ? (
                                <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1 }}>
                                    Aktuell gesetzt: {existingHeaderImage}
                                </Typography>
                            ) : null}

                            {removeImage ? (
                                <Alert severity="warning" sx={{ mt: 1 }}>
                                    Bild wird beim Speichern entfernt.
                                </Alert>
                            ) : null}
                        </Box>

                        <Box
                            sx={{
                                border: "1px solid",
                                borderColor: "divider",
                                borderRadius: 1,
                                overflow: "hidden",
                                bgcolor: "background.default",
                                minHeight: 120,
                            }}
                        >
                            {imagePreview ? (
                                <img
                                    src={imagePreview}
                                    alt="Header preview"
                                    style={{ display: "block", width: "100%", height: "auto" }}
                                />
                            ) : existingHeaderImage && !removeImage ? (
                                <img
                                    src={buildApiUrl(existingHeaderImage)}
                                    alt="Header current"
                                    style={{ display: "block", width: "100%", height: "auto" }}
                                />
                            ) : (
                                <Box sx={{ p: 2 }}>
                                    <Typography variant="body2" color="text.secondary">
                                        Kein Bild gesetzt
                                    </Typography>
                                </Box>
                            )}
                        </Box>
                    </Box>

                    {submitError && <Alert severity="error" sx={{ mt: 2 }}>{submitError}</Alert>}
                </DialogContent>

                <DialogActions>
                    <Button onClick={() => setOpen(false)} disabled={submitting}>Abbrechen</Button>
                    <Button variant="contained" onClick={handleSave} disabled={submitting}>
                        {submitting ? "Speichern…" : "Speichern"}
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog
                open={deleteDialogOpen}
                onClose={() => !deleting && setDeleteDialogOpen(false)}
                fullWidth
                maxWidth="xs"
            >
                <DialogTitle>Domain löschen</DialogTitle>
                <DialogContent dividers>
                    {deleteError ? (
                        <Alert severity="error">{deleteError}</Alert>
                    ) : (
                        <Alert severity="warning">
                            Willst du die Domain <b>{deleteTarget?.url}</b> wirklich löschen?
                        </Alert>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeleteDialogOpen(false)} disabled={deleting}>
                        Abbrechen
                    </Button>
                    <Button
                        color="error"
                        variant="contained"
                        disabled={deleting}
                        onClick={async () => {
                            if (!deleteTarget) return;
                            setDeleting(true);
                            setDeleteError("");
                            try {
                                await axios.delete(buildApiUrl(`/api/domains/${deleteTarget.id}`));
                                setDeleteDialogOpen(false);
                                await fetchDomains();
                            } catch (e) {
                                setDeleteError(e?.response?.data?.error || e.message || "Löschen fehlgeschlagen.");
                            } finally {
                                setDeleting(false);
                            }
                        }}
                    >
                        {deleting ? "Löschen…" : "Löschen"}
                    </Button>
                </DialogActions>
            </Dialog>
        </PageContainer>
    );
}
